import { RecordCollection } from './record-collection.js';
import { ListFilterBar } from './data-view.js';
import { SegmentedControl, IconButton } from '@hue/design-system';
import {
  Button,
  Card,
  Input,
  Modal,
  StatusPill,
  StatusText,
  useSnackbar,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, MapPin, PencilLine, LogIn, Archive, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatPhone } from '@hue/contracts';
import {
  businessUnitApi,
  companyIdentityApi,
  getApiError,
  type BusinessUnit,
  type BusinessUnitKind,
} from '../lib/api.js';
import { brandQueryKey, EMPTY_LOGOS } from './brand.js';
import { BusinessUnitDialog } from './business-unit-dialog.js';
import { refreshSession } from '../lib/session.js';
import { useCompanyScope } from './company-context.js';
import { CreateButton, PageHeader } from './page-title.js';

type ActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

const kindLabels: Record<BusinessUnitKind, string> = {
  ESTABLISHMENT: 'Estabelecimento',
  SUPPORT: 'Apoio',
};
const kindHints: Record<BusinessUnitKind, string> = {
  ESTABLISHMENT: 'Atende clientes, tem agenda e ocupa uma vaga do plano.',
  SUPPORT: 'Depósito ou escritório: guarda estoque, não tem agenda e fica fora do limite do plano.',
};

function keyFrom(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .slice(0, 63);
}

export function CompanyBusinessUnits() {
  const { company, can } = useCompanyScope();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityFilter>('ALL');
  const [formOpen, setFormOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [key, setKey] = useState('');
  const [kind, setKind] = useState<BusinessUnitKind>('ESTABLISHMENT');
  const [formError, setFormError] = useState<string>();
  const management = useQuery({
    queryKey: ['tenant', 'business-units'],
    queryFn: businessUnitApi.management,
  });
  const canManage = can('business_units.manage');
  const [editing, setEditing] = useState<BusinessUnit>();
  const brand = useQuery({
    queryKey: [...brandQueryKey, 'units'],
    queryFn: companyIdentityApi.brand,
    enabled: canManage,
  });
  const units = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    return (management.data?.units ?? [])
      .filter((unit) =>
        activity === 'ACTIVE' ? unit.active : activity === 'INACTIVE' ? !unit.active : true,
      )
      .filter((unit) =>
        term ? `${unit.displayName} ${unit.key}`.toLocaleLowerCase('pt-BR').includes(term) : true,
      )
      .toSorted((left, right) => left.displayName.localeCompare(right.displayName, 'pt-BR'));
  }, [activity, management.data?.units, search]);
  const create = useMutation({
    mutationFn: businessUnitApi.create,
    onSuccess: async (result) => {
      queryClient.setQueryData(['tenant', 'business-units'], result);
      await refreshSession(queryClient);
      setFormOpen(false);
      snackbar.success('Unidade criada.');
    },
    onError: (error) => setFormError(getApiError(error)),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      businessUnitApi.setActive(id, active),
    onSuccess: async (result) => {
      queryClient.setQueryData(['tenant', 'business-units'], result);
      await refreshSession(queryClient);
    },
    onError: (error) => snackbar.failure(getApiError(error)),
  });

  async function openUnit(id: string) {
    try {
      await businessUnitApi.switch(id);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      await refreshSession(queryClient);
      await navigate({ to: '/companies/$companyKey', params: { companyKey: company.key } });
    } catch (error) {
      snackbar.failure(getApiError(error));
    }
  }

  const activeUnits = management.data?.units.filter((unit) => unit.active) ?? [];
  const establishments = activeUnits.filter((unit) => unit.kind === 'ESTABLISHMENT').length;
  const supports = activeUnits.length - establishments;
  /// Sem vaga de estabelecimento ainda se cria unidade de apoio, que fica fora do limite.
  const limit = management.data?.activeLimit;
  const establishmentFull = limit !== undefined && limit !== null && establishments >= limit;
  const creationBlocked = !management.data;

  function unitActions(unit: BusinessUnit) {
    return (
      <div className="flex flex-wrap justify-end gap-1">
        {canManage ? (
          <IconButton label="Editar dados" icon={<PencilLine />} onClick={() => setEditing(unit)} />
        ) : null}
        {unit.active ? (
          <IconButton
            label="Acessar unidade"
            icon={<LogIn />}
            onClick={() => void openUnit(unit.id)}
          />
        ) : null}
        {canManage ? (
          <IconButton
            label={unit.active ? 'Desativar' : 'Reativar'}
            icon={unit.active ? <Archive /> : <RotateCcw />}
            tone={unit.active ? 'danger' : 'neutral'}
            loading={toggle.isPending && toggle.variables?.id === unit.id}
            onClick={() => toggle.mutate({ id: unit.id, active: !unit.active })}
          />
        ) : null}
      </div>
    );
  }
  return (
    <section aria-labelledby="business-units-title">
      <PageHeader
        trail="Configurações / Unidades"
        id="business-units-title"
        title="Unidades"
        description={
          <>Gerencie os estabelecimentos e entre em uma unidade para visualizar seus dados.</>
        }
        actions={
          <>
            {canManage ? (
              <CreateButton
                disabled={creationBlocked}
                onClick={() => {
                  setDisplayName('');
                  setKey('');
                  setKind(establishmentFull ? 'SUPPORT' : 'ESTABLISHMENT');
                  setFormError(undefined);
                  setFormOpen(true);
                }}
              >
                Nova unidade
              </CreateButton>
            ) : null}
          </>
        }
      />

      {management.data ? (
        <div className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary">
          {limit === null
            ? `${establishments} ${
                establishments === 1 ? 'estabelecimento ativo' : 'estabelecimentos ativos'
              }, sem limite contratado`
            : `${establishments} de ${limit} ${
                limit === 1
                  ? 'estabelecimento ativo contratado'
                  : 'estabelecimentos ativos contratados'
              }`}
          {supports > 0
            ? ` · ${supports} ${supports === 1 ? 'unidade de apoio' : 'unidades de apoio'}, fora do limite`
            : ''}
          .
        </div>
      ) : null}

      <ListFilterBar
        searchLabel="Filtrar unidades"
        searchPlaceholder="Nome ou chave"
        search={search}
        onSearchChange={setSearch}
        count={`${units.length} ${units.length === 1 ? 'unidade' : 'unidades'}`}
      >
        <SegmentedControl
          label="Filtrar por situação"
          compact
          value={activity}
          onChange={setActivity}
          options={[
            { value: 'ALL', label: 'Todas' },
            { value: 'ACTIVE', label: 'Ativas' },
            { value: 'INACTIVE', label: 'Inativas' },
          ]}
        />
      </ListFilterBar>

      {management.isLoading ? (
        <Card>Carregando unidades…</Card>
      ) : management.isError ? (
        <Card>
          <p role="alert" className="text-danger">
            {getApiError(management.error)}
          </p>
        </Card>
      ) : units.length ? (
        <RecordCollection
          label="Unidades"
          records={units}
          recordKey={(unit) => unit.id}
          columns={[
            {
              key: 'name',
              label: 'Unidade',
              value: (unit) => unit.displayName,
              render: (unit) => <strong>{unit.displayName}</strong>,
            },
            {
              key: 'kind',
              label: 'Tipo',
              value: (unit) => kindLabels[unit.kind],
              render: (unit) => (
                <span>
                  {kindLabels[unit.kind]} · <span className="font-mono">{unit.key}</span>
                </span>
              ),
            },
            {
              key: 'contact',
              label: 'Dados e contato',
              render: (unit) => (
                <UnitContact unit={unit} ownLogo={Boolean(brand.data?.units[unit.id])} />
              ),
            },
            {
              key: 'status',
              label: 'Situação',
              value: (unit) => Number(unit.active),
              render: (unit) => (
                <StatusPill tone={unit.active ? 'positive' : 'neutral'}>
                  {unit.active ? 'Ativa' : 'Inativa'}
                </StatusPill>
              ),
            },
          ]}
          actions={unitActions}
          card={(unit) => (
            <Card key={unit.id} className="p-5">
              <div className="flex items-start justify-between gap-3">
                <span className="grid size-10 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                  <Building2 className="size-5" />
                </span>
                <StatusPill tone={unit.active ? 'positive' : 'neutral'}>
                  {unit.active ? 'Ativa' : 'Inativa'}
                </StatusPill>
              </div>
              <h2 className="mt-4 text-lg font-semibold">{unit.displayName}</h2>
              <p className="mt-1 text-xs text-content-faint">
                {kindLabels[unit.kind]} · <span className="font-mono">{unit.key}</span>
              </p>
              <UnitContact unit={unit} ownLogo={Boolean(brand.data?.units[unit.id])} />
              {unitActions(unit)}
            </Card>
          )}
        />
      ) : (
        <Card>Nenhuma unidade corresponde ao filtro.</Card>
      )}

      {editing ? (
        <BusinessUnitDialog
          key={editing.id}
          unit={editing}
          logos={brand.data?.units[editing.id]}
          companyLogos={brand.data?.company ?? EMPTY_LOGOS}
          onClose={() => setEditing(undefined)}
          onSaved={(result) => queryClient.setQueryData(['tenant', 'business-units'], result)}
        />
      ) : null}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title="Nova unidade"
        description="A unidade nasce ativa. Só estabelecimento ocupa vaga do plano."
      >
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            setFormError(undefined);
            create.mutate({ displayName, key, kind });
          }}
        >
          <Input
            label="Nome da unidade"
            value={displayName}
            onChange={(event) => {
              const value = event.target.value;
              setDisplayName(value);
              setKey(keyFrom(value));
            }}
          />
          <Input label="Chave" value={key} onChange={(event) => setKey(event.target.value)} />
          <div>
            <p className="mb-2 text-sm font-medium text-content-secondary" id="unit-kind-label">
              Tipo
            </p>
            <SegmentedControl
              label="Tipo"
              value={kind}
              onChange={setKind}
              options={(['ESTABLISHMENT', 'SUPPORT'] as const).map((value) => ({
                value,
                label: kindLabels[value],
              }))}
            />
            <p className="mt-2 text-xs text-content-faint">{kindHints[kind]}</p>
            {kind === 'ESTABLISHMENT' && establishmentFull ? (
              <p className="mt-1 text-xs text-warning">
                O plano não tem vaga para outro estabelecimento.
              </p>
            ) : null}
          </div>
          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3 border-t border-surface-border pt-5">
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              Cancelar
            </Button>
            <Button loading={create.isPending}>Criar unidade</Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

/// Onde a unidade fica e como falar com ela, como sai no timbre. Sem cidade, avisa: o documento
/// vai deixar o espaço em branco para escrever à mão (não bloqueia nada).
function UnitContact({ unit, ownLogo }: { unit: BusinessUnit; ownLogo: boolean }) {
  const place = [unit.address?.city, unit.address?.state].filter(Boolean).join(' – ');
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-content-muted">
      {place ? (
        <span className="inline-flex items-center gap-1">
          <MapPin className="size-3.5" aria-hidden="true" />
          {place}
        </span>
      ) : (
        <StatusText tone="warning">Sem endereço</StatusText>
      )}
      {unit.phone ? <span>{formatPhone(unit.phone)}</span> : null}
      {ownLogo ? <StatusText tone="brand">Logo própria</StatusText> : null}
    </div>
  );
}
